<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Header Section -->
    <div class="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div class="px-6 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <button
              class="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              @click="$router.go(-1)"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span>Back</span>
            </button>
            <div class="flex items-center space-x-2">
              <div
                class="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center"
              >
                <svg
                  class="w-6 h-6 text-blue-600 dark:text-blue-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <div>
                <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
                  🔧 {{ workloadName }}
                </h1>
                <p class="text-sm text-gray-500 dark:text-gray-400">Workload Details</p>
              </div>
            </div>
          </div>
          <div class="flex items-center space-x-3">
            <button
              class="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              @click="refreshData"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="p-6">
      <!-- Loading State -->
      <div
        v-if="loading"
        class="flex items-center justify-center py-24"
      >
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span class="ml-3 text-gray-600 dark:text-gray-400 text-lg">
          Loading workload details...
        </span>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="flex flex-col items-center justify-center py-24"
      >
        <div class="text-red-500 mb-4">
          <svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <p class="text-xl text-gray-600 dark:text-gray-400">{{ error }}</p>
        <button
          class="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          @click="loadWorkloadDetails"
        >
          Try Again
        </button>
      </div>

      <!-- Content -->
      <div
        v-else-if="workloadDetail"
        class="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        <!-- Overview Section -->
        <div
          class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <div class="p-6">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <span class="mr-2">📦</span> Overview
            </h2>
            <div class="space-y-4">
              <div class="flex justify-between items-start">
                <span class="text-sm text-gray-600 dark:text-gray-400">Workload ID:</span>
                <span class="text-sm font-mono text-gray-900 dark:text-white text-right">
                  {{ workloadDetail.workloadId }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-600 dark:text-gray-400">App Name:</span>
                <span class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ workloadDetail.appName }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-600 dark:text-gray-400">Git Repository:</span>
                <span class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ workloadDetail.gitRepo }}
                </span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600 dark:text-gray-400">Environment:</span>
                <span
                  :class="[
                    'inline-flex items-center px-3 py-1 rounded-md text-sm font-medium',
                    workloadDetail.environment === 'interop'
                      ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
                      : workloadDetail.environment === 'staging'
                        ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                  ]"
                >
                  {{ workloadDetail.environment }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-600 dark:text-gray-400">Package Count:</span>
                <span class="text-sm font-semibold text-blue-600 dark:text-blue-400">
                  {{ workloadDetail.packageCount }} packages
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Latest Deployment Section -->
        <div
          class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <div class="p-6">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <span class="mr-2">🚀</span> Latest Deployment
            </h2>
            <div class="space-y-4">
              <div class="flex justify-between items-start">
                <span class="text-sm text-gray-600 dark:text-gray-400">Last Deployed:</span>
                <span class="text-sm font-medium text-gray-900 dark:text-white text-right">
                  {{ formatDate(workloadDetail.lastDeployed) }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-600 dark:text-gray-400">Build Number:</span>
                <span class="text-sm font-semibold text-blue-600 dark:text-blue-400">
                  #{{ workloadDetail.buildNumber }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-600 dark:text-gray-400">Build ID:</span>
                <span class="text-sm font-mono text-gray-900 dark:text-white">
                  {{ workloadDetail.buildId }}
                </span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600 dark:text-gray-400">Triggered By:</span>
                <div class="flex items-center space-x-2">
                  <div
                    class="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center"
                  >
                    <span class="text-xs font-bold text-white">
                      {{ getUserInitials(workloadDetail.buildUserEmail) }}
                    </span>
                  </div>
                  <span class="text-sm text-gray-900 dark:text-white">
                    {{ workloadDetail.buildUserEmail }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Git Information Section -->
        <div
          class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 lg:col-span-2"
        >
          <div class="p-6">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <span class="mr-2">🌿</span> Git Information
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div class="flex justify-between items-center mb-2">
                  <span class="text-sm text-gray-600 dark:text-gray-400">Branch:</span>
                  <span
                    class="text-sm font-medium text-gray-900 dark:text-white font-mono bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-md"
                  >
                    {{ workloadDetail.branch }}
                  </span>
                </div>
              </div>
              <div>
                <div class="flex flex-col">
                  <div class="flex justify-between items-center mb-2">
                    <span class="text-sm text-gray-600 dark:text-gray-400">Commit Hash:</span>
                    <button
                      class="text-xs px-3 py-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 bg-blue-50 dark:bg-blue-900/20 rounded flex items-center space-x-1 transition-colors"
                      @click="copyCommitHash"
                    >
                      <svg
                        class="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      <span>{{ copied ? 'Copied!' : 'Copy' }}</span>
                    </button>
                  </div>
                  <div
                    class="text-xs font-mono text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 px-4 py-3 rounded-lg break-all border border-gray-200 dark:border-gray-700"
                  >
                    {{ workloadDetail.commitId }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Packages Section -->
        <div
          v-if="workloadDetail.packages && Object.keys(workloadDetail.packages).length > 0"
          class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 lg:col-span-2"
        >
          <div class="p-6">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <span class="mr-2">📦</span>
                Packages ({{ Object.keys(workloadDetail.packages).length }})
              </h2>
              <div class="flex items-center space-x-3">
                <button
                  v-if="workloadDetail.depTree"
                  class="flex items-center space-x-2 px-4 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  @click="showDepTreeModal = true"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                    />
                  </svg>
                  <span>Tree View</span>
                </button>
                <button
                  v-if="workloadDetail.depTree"
                  class="flex items-center space-x-2 px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  @click="showD3Modal = true"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  <span>Graph View</span>
                </button>
                <input
                  v-model="packageSearchQuery"
                  type="text"
                  placeholder="Search packages..."
                  class="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <!-- Packages Table -->
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    <th
                      class="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Package Name
                    </th>
                    <th
                      class="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Direct Version
                    </th>
                    <th
                      class="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider"
                    >
                      All Versions
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                  <tr
                    v-for="(versions, packageName) in filteredPackages"
                    :key="packageName"
                    class="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                  >
                    <td class="px-4 py-3">
                      <button
                        class="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline text-left"
                        @click="navigateToPackage(packageName)"
                      >
                        {{ packageName }}
                      </button>
                    </td>
                    <td class="px-4 py-3">
                      <span
                        v-if="getDirectVersion(versions)"
                        class="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 font-semibold"
                      >
                        {{ getDirectVersion(versions) }}
                        <span class="ml-1">✓</span>
                      </span>
                      <span
                        v-else
                        class="text-sm text-gray-400 dark:text-gray-500"
                      >
                        -
                      </span>
                    </td>
                    <td class="px-4 py-3">
                      <div class="flex items-center space-x-2">
                        <div class="flex flex-wrap gap-1.5">
                          <span
                            v-for="(version, idx) in getTransitiveVersions(versions).slice(0, 3)"
                            :key="idx"
                            class="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                          >
                            {{ version }}
                          </span>
                        </div>
                        <button
                          v-if="getTransitiveVersions(versions).length > 3"
                          class="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline whitespace-nowrap"
                          @click="togglePackageExpansion(packageName)"
                        >
                          {{
                            expandedPackages.has(packageName)
                              ? 'Show less'
                              : `+${getTransitiveVersions(versions).length - 3} more`
                          }}
                        </button>
                      </div>
                      <div
                        v-if="expandedPackages.has(packageName) && getTransitiveVersions(versions).length > 3"
                        class="flex flex-wrap gap-1.5 mt-2"
                      >
                        <span
                          v-for="(version, idx) in getTransitiveVersions(versions).slice(3)"
                          :key="idx"
                          class="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                        >
                          {{ version }}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- No Results -->
              <div
                v-if="Object.keys(filteredPackages).length === 0"
                class="text-center py-12"
              >
                <p class="text-gray-500 dark:text-gray-400">
                  No packages found matching "{{ packageSearchQuery }}"
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dependency Tree Modal -->
    <div
      v-if="showDepTreeModal"
      class="fixed inset-0 z-50 overflow-y-auto"
      @click.self="showDepTreeModal = false"
    >
      <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div
          class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          @click="showDepTreeModal = false"
        ></div>

        <!-- Modal panel -->
        <div
          class="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl"
          @click.stop
        >
          <!-- Header -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 class="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <svg
                class="w-6 h-6 mr-2 text-purple-600 dark:text-purple-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                />
              </svg>
              Dependency Tree
            </h3>
            <button
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              @click="showDepTreeModal = false"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div class="px-6 py-4 max-h-[70vh] overflow-y-auto">
            <div
              class="font-mono text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-900 p-6 rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <pre class="whitespace-pre overflow-x-auto pl-4">{{ formattedDepTree }}</pre>
            </div>
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-end px-6 py-4 border-t border-gray-200 dark:border-gray-700 space-x-3">
            <button
              class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              @click="showDepTreeModal = false"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- D3.js Graph Modal -->
    <div
      v-if="showD3Modal"
      class="fixed inset-0 z-50 overflow-y-auto"
      @click.self="showD3Modal = false"
    >
      <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div
          class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          @click="showD3Modal = false"
        ></div>

        <!-- Modal panel -->
        <div
          class="inline-block w-full max-w-6xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl"
          @click.stop
        >
          <!-- Header -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 class="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <svg
                class="w-6 h-6 mr-2 text-indigo-600 dark:text-indigo-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              Dependency Graph
            </h3>
            <button
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              @click="showD3Modal = false"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div class="px-6 py-4">
            <div
              ref="d3Container"
              class="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
              style="height: 70vh;"
            ></div>
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div class="text-sm text-gray-600 dark:text-gray-400">
              💡 Tip: Scroll to zoom, drag to pan, click nodes to highlight dependencies
            </div>
            <button
              class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              @click="showD3Modal = false"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { workloadService } from '../../services/workload'
import type { WorkloadDetail } from '../../types/workload'
import { logger } from '../../utils/logger'
import * as d3 from 'd3'

const route = useRoute()
const router = useRouter()

const workloadDetail = ref<WorkloadDetail | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const copied = ref(false)
const packageSearchQuery = ref('')
const expandedPackages = ref<Set<string>>(new Set())
const showDepTreeModal = ref(false)
const showD3Modal = ref(false)
const d3Container = ref<HTMLElement | null>(null)

// Get route params
const workloadName = computed(() => route.params.workload as string)
const gitRepo = computed(() => route.params.gitRepo as string)
const environment = computed(() => route.params.environment as string)

// Filtered packages based on search query
const filteredPackages = computed(() => {
  if (!workloadDetail.value?.packages) return {}

  const query = packageSearchQuery.value.toLowerCase().trim()
  if (!query) return workloadDetail.value.packages

  const filtered: Record<string, string[]> = {}
  Object.entries(workloadDetail.value.packages).forEach(([packageName, versions]) => {
    if (packageName.toLowerCase().includes(query)) {
      filtered[packageName] = versions
    }
  })
  return filtered
})

// Interface for dependency tree node
interface DepTreeNode {
  name: string
  children: DepTreeNode[]
}

// Format dependency tree for display
const formattedDepTree = computed(() => {
  if (!workloadDetail.value?.depTree) return ''

  try {
    const tree: DepTreeNode[] = JSON.parse(workloadDetail.value.depTree)
    return formatTreeNodes(tree, '', true)
  } catch (err) {
    logger.error('Failed to parse dependency tree:', err)
    return 'Error parsing dependency tree'
  }
})

// Recursive function to format tree nodes (yarn list style)
const formatTreeNodes = (nodes: DepTreeNode[], prefix: string, isRoot: boolean): string => {
  let result = ''
  nodes.forEach((node, index) => {
    const isLast = index === nodes.length - 1
    
    if (isRoot) {
      // Root level items
      result += `├─ ${node.name}\n`
      if (node.children && node.children.length > 0) {
        result += formatTreeNodes(node.children, '│  ', false)
      }
    } else {
      // Nested items
      const connector = isLast ? '└─ ' : '├─ '
      const childPrefix = isLast ? '   ' : '│  '
      
      result += `${prefix}${connector}${node.name}\n`
      
      if (node.children && node.children.length > 0) {
        result += formatTreeNodes(node.children, `${prefix}${childPrefix}`, false)
      }
    }
  })
  return result
}

const loadWorkloadDetails = async () => {
  if (!workloadName.value || !gitRepo.value || !environment.value) {
    error.value = 'Missing required parameters'
    return
  }

  try {
    loading.value = true
    error.value = null

    const response = await workloadService.getWorkloadDetails({
      workload: workloadName.value,
      gitRepo: gitRepo.value,
      environment: environment.value,
      limit: 1
    })
    logger.log('Workload details response:', response)
    // @ts-ignore
    if (response && response.length > 0) {
      // @ts-ignore
      workloadDetail.value = response[0]
    } else {
      error.value = 'No workload details found'
    }
  } catch (err) {
    logger.error('Failed to load workload details:', err)
    error.value = 'Failed to load workload details. Please try again.'
  } finally {
    loading.value = false
  }
}

const refreshData = () => {
  loadWorkloadDetails()
}

const copyCommitHash = async () => {
  if (workloadDetail.value?.commitId) {
    try {
      await navigator.clipboard.writeText(workloadDetail.value.commitId)
      copied.value = true
      setTimeout(() => {
        copied.value = false
      }, 2000)
    } catch (err) {
      logger.error('Failed to copy commit hash:', err)
    }
  }
}

const getUserInitials = (email: string): string => {
  if (!email) return '?'
  const parts = email.split('@')[0].split('.')
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return email.substring(0, 2).toUpperCase()
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffHrs = Math.floor(diffMs / (1000 * 60 * 60))

  let relative = ''
  if (diffHrs < 1) {
    relative = 'just now'
  } else if (diffHrs < 24) {
    relative = `${diffHrs} hour${diffHrs !== 1 ? 's' : ''} ago`
  } else {
    const diffDays = Math.floor(diffHrs / 24)
    relative = `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
  }

  const formatted = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })

  return `${formatted} (${relative})`
}

// Package helper functions
const getDirectVersion = (versions: string[]): string | null => {
  const directVersion = versions.find((v) => v.includes('(direct)'))
  return directVersion ? directVersion.replace(' (direct)', '').trim() : null
}

const getTransitiveVersions = (versions: string[]): string[] => {
  return versions.filter((v) => !v.includes('(direct)')).map((v) => v.trim())
}

const togglePackageExpansion = (packageName: string) => {
  if (expandedPackages.value.has(packageName)) {
    expandedPackages.value.delete(packageName)
  } else {
    expandedPackages.value.add(packageName)
  }
  // Trigger reactivity
  expandedPackages.value = new Set(expandedPackages.value)
}

const navigateToPackage = (packageName: string) => {
  router.push(`/packages/${encodeURIComponent(packageName)}`)
}

// D3.js Tree Rendering
const renderD3Tree = () => {
  if (!d3Container.value || !workloadDetail.value?.depTree) return

  try {
    const treeData: DepTreeNode[] = JSON.parse(workloadDetail.value.depTree)

    // Clear previous content
    d3.select(d3Container.value).selectAll('*').remove()

    const container = d3Container.value
    const width = container.clientWidth
    const height = container.clientHeight

    // Create SVG
    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('class', 'w-full h-full')

    const g = svg.append('g')

    // Create zoom behavior
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 3])
      .on('zoom', (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
        g.attr('transform', event.transform.toString())
      })

    svg.call(zoom as any)

    // Convert flat array to hierarchical structure
    const root = d3.hierarchy({ name: 'root', children: treeData } as any)

    // Create tree layout with more vertical spacing
    const treeLayout = d3
      .tree<any>()
      .size([height * 2, width - 200])
      .separation((a, b) => (a.parent === b.parent ? 3 : 4))

    treeLayout(root)

    // Draw links
    g.selectAll('.link')
      .data(root.links())
      .enter()
      .append('path')
      .attr('class', 'link')
      .attr('fill', 'none')
      .attr('stroke', '#94a3b8')
      .attr('stroke-width', 2)
      .attr(
        'd',
        d3
          .linkHorizontal<any, any>()
          .x((d: any) => d.y)
          .y((d: any) => d.x)
      )

    // Draw nodes
    const nodes = g
      .selectAll('.node')
      .data(root.descendants())
      .enter()
      .append('g')
      .attr('class', 'node')
      .attr('transform', (d: any) => `translate(${d.y},${d.x})`)
      .style('cursor', 'pointer')
      .on('click', function (event: any, d: any) {
        if (d.data.name && d.data.name !== 'root') {
          // Highlight this node and its connections
          d3.selectAll('.node circle').attr('fill', '#6366f1').attr('r', 8)
          d3.selectAll('.link').attr('stroke', '#94a3b8').attr('stroke-width', 2)

          // Highlight selected node
          // eslint-disable-next-line @typescript-eslint/no-this-alias
          const self = this
          d3.select(self).select('circle').attr('fill', '#f59e0b').attr('r', 10)

          // Highlight connected links
          const nodeId = d.data.name
          d3.selectAll('.link')
            .filter((l: any) => l.source.data.name === nodeId || l.target.data.name === nodeId)
            .attr('stroke', '#f59e0b')
            .attr('stroke-width', 3)
        }
      })

    // Add circles for nodes
    nodes
      .append('circle')
      .attr('r', 8)
      .attr('fill', '#6366f1')
      .attr('stroke', '#fff')
      .attr('stroke-width', 2.5)

    // Add labels
    nodes
      .append('text')
      .attr('dy', '.35em')
      .attr('x', (d: any) => (d.children ? -15 : 15))
      .attr('text-anchor', (d: any) => (d.children ? 'end' : 'start'))
      .attr('font-size', '13px')
      .attr('fill', '#1f2937')
      .attr('class', 'dark:fill-gray-200')
      .text((d: any) => {
        if (d.data.name === 'root') return ''
        // Extract package name and version
        const name = d.data.name
        const parts = name.split('@')
        if (parts.length > 1) {
          return `${parts.slice(0, -1).join('@')}@${parts[parts.length - 1]}`
        }
        return name
      })

    // Center the tree
    const bounds = g.node()?.getBBox()
    if (bounds) {
      const fullWidth = bounds.width
      const fullHeight = bounds.height
      const midX = bounds.x + fullWidth / 2
      const midY = bounds.y + fullHeight / 2

      svg.call(
        zoom.transform as any,
        d3.zoomIdentity.translate(width / 2 - midX, height / 2 - midY).scale(0.4)
      )
    }
  } catch (err) {
    logger.error('Failed to render D3 tree:', err)
  }
}

// Watch for D3 modal opening
watch(showD3Modal, async (newVal) => {
  if (newVal) {
    await nextTick()
    renderD3Tree()
  }
})

// Lifecycle
onMounted(() => {
  loadWorkloadDetails()
})
</script>

<style scoped>
/* Custom styles for the workload details page */
</style>
